import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Link } from "wouter";
import {
  MapPin, Clock, Users, Zap, Heart, TrendingUp,
  MessageCircle, ArrowRight, Briefcase, Instagram, Star,
} from "lucide-react";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

const ROLES = [
  {
    id: "sales-intern",
    title: "Sales Representative Intern",
    type: "Internship",
    location: "Lagos, Nigeria (Hybrid)",
    department: "Sales & Growth",
    icon: TrendingUp,
    iconColor: "text-emerald-600",
    iconBg: "bg-emerald-50",
    badgeColor: "bg-emerald-100 text-emerald-800",
    summary:
      "Join our lean sales team and help us bring RetailPulse to more Nigerian retailers. You'll be on the ground reaching out to shops, markets, and small businesses — and seeing your impact in real revenue.",
    responsibilities: [
      "Identify and reach out to prospective retail customers (supermarkets, fashion, food vendors, salons)",
      "Give product demos via WhatsApp, phone calls, or in-person visits",
      "Follow up on leads and help close subscriptions",
      "Gather feedback from prospects and share insights with the product team",
      "Help onboard new customers and ensure they're set up successfully",
      "Track your pipeline and report weekly progress",
    ],
    requirements: [
      "Passion for sales and talking to people — this is mostly a people role",
      "Good communication skills in English (Pidgin is a bonus)",
      "Smartphone with reliable internet access",
      "Ability to work independently and meet targets",
      "Interest in tech and small business — understanding retailer pain points helps",
      "Lagos-based (or willing to work remotely with occasional in-person days)",
    ],
    perks: [
      "Competitive monthly stipend + commission on closed deals",
      "Mentorship from our founding team",
      "Certificate of internship + LinkedIn recommendation",
      "Opportunity for full-time role based on performance",
      "Flexible hours — results matter more than clocking in",
    ],
  },
  {
    id: "social-media-intern",
    title: "Social Media Marketer Intern",
    type: "Internship",
    location: "Remote (Nigeria)",
    department: "Marketing",
    icon: Instagram,
    iconColor: "text-pink-600",
    iconBg: "bg-pink-50",
    badgeColor: "bg-pink-100 text-pink-800",
    summary:
      "We need someone who lives and breathes social media — someone who can turn our product updates, customer wins, and retail tips into content that Nigerian business owners stop scrolling for.",
    responsibilities: [
      "Plan and execute content across Instagram, Facebook, TikTok, and WhatsApp Status",
      "Create short-form videos, carousels, reels, and infographics about RetailPulse features",
      "Write engaging captions that speak directly to Nigerian retailers",
      "Engage with our audience — reply to comments, DMs, and mentions",
      "Monitor trends (naija market trends, retail news) and pitch relevant content ideas",
      "Track metrics (reach, engagement, follower growth) and report weekly",
      "Collaborate with the product team to announce new features and updates",
    ],
    requirements: [
      "Active and creative on social media — show us your personal or prior work",
      "Good grasp of Instagram Reels, TikTok, and WhatsApp Business",
      "Basic graphic design or video editing skills (Canva is totally fine)",
      "Ability to write in a relatable, conversational Nigerian voice",
      "Understanding of small business culture in Nigeria",
      "Self-motivated and able to work with minimal supervision",
    ],
    perks: [
      "Monthly stipend + performance bonus for growth milestones",
      "Creative freedom — we trust your instincts",
      "Certificate of internship + portfolio-worthy work",
      "Mentorship from the founding team",
      "Path to full-time role for top performers",
    ],
  },
];

const VALUES = [
  {
    icon: Zap,
    title: "Move fast",
    desc: "We ship weekly. You'll see your work go live in days, not months.",
  },
  {
    icon: Heart,
    title: "Customer obsessed",
    desc: "Every feature we build is driven by a real retailer's real problem.",
  },
  {
    icon: Users,
    title: "Small team, big impact",
    desc: "No bureaucracy. Your work matters from day one.",
  },
  {
    icon: Star,
    title: "Grow with us",
    desc: "We're early. The best interns become full-time team members.",
  },
];

export default function Careers() {
  return (
    <div className="min-h-screen bg-background">
      {/* Nav */}
      <header className="border-b sticky top-0 z-50 bg-background/95 backdrop-blur">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link href={`${BASE}/`}>
            <span className="font-bold text-lg text-primary cursor-pointer">RetailPulse</span>
          </Link>
          <div className="flex items-center gap-3">
            <Link href={`${BASE}/login`}>
              <Button variant="ghost" size="sm">Sign in</Button>
            </Link>
            <Link href={`${BASE}/signup`}>
              <Button size="sm">Get started free</Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-gradient-to-br from-primary/5 via-background to-background border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-20 text-center">
          <Badge className="mb-6 bg-primary/10 text-primary border-primary/20 hover:bg-primary/10">
            <Briefcase className="w-3 h-3 mr-1" /> We're hiring
          </Badge>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mb-5">
            Help us power <span className="text-primary">Nigerian retail</span>
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            RetailPulse is building the go-to POS and inventory platform for Nigerian retailers.
            We're a small, fast-moving team and we're looking for hungry interns to grow with us.
          </p>
        </div>
      </section>

      {/* Values */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-16">
        <h2 className="text-2xl font-bold mb-2 text-center">Why join RetailPulse?</h2>
        <p className="text-muted-foreground text-center mb-10">We're small on purpose. Here's what that means for you.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {VALUES.map(v => (
            <div key={v.title} className="text-center p-6 rounded-2xl bg-muted/40 border">
              <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                <v.icon className="w-6 h-6 text-primary" />
              </div>
              <h3 className="font-semibold mb-2">{v.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{v.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Open roles */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 pb-20">
        <h2 className="text-2xl font-bold mb-2">Open positions</h2>
        <p className="text-muted-foreground mb-10">Both roles are internships — paid, with a clear path to full-time.</p>

        <div className="space-y-8">
          {ROLES.map(role => {
            const Icon = role.icon;
            return (
              <Card key={role.id} className="overflow-hidden">
                <CardHeader className="pb-4">
                  <div className="flex flex-wrap items-start gap-4">
                    <div className={`w-12 h-12 ${role.iconBg} rounded-xl flex items-center justify-center shrink-0`}>
                      <Icon className={`w-6 h-6 ${role.iconColor}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <CardTitle className="text-xl">{role.title}</CardTitle>
                        <Badge className={`${role.badgeColor} border-0 text-xs font-medium`}>
                          {role.type}
                        </Badge>
                      </div>
                      <CardDescription className="flex flex-wrap gap-3 text-xs">
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3 h-3" /> {role.location}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" /> Part-time / Flexible hours
                        </span>
                        <span className="flex items-center gap-1">
                          <Briefcase className="w-3 h-3" /> {role.department}
                        </span>
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>

                <CardContent className="space-y-6">
                  <p className="text-muted-foreground leading-relaxed">{role.summary}</p>

                  <div className="grid md:grid-cols-3 gap-6">
                    <div>
                      <h4 className="font-semibold text-sm mb-3 text-foreground">What you'll do</h4>
                      <ul className="space-y-2">
                        {role.responsibilities.map(r => (
                          <li key={r} className="text-sm text-muted-foreground flex gap-2">
                            <ArrowRight className="w-3.5 h-3.5 text-primary shrink-0 mt-0.5" />
                            {r}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-semibold text-sm mb-3 text-foreground">What we're looking for</h4>
                      <ul className="space-y-2">
                        {role.requirements.map(r => (
                          <li key={r} className="text-sm text-muted-foreground flex gap-2">
                            <ArrowRight className="w-3.5 h-3.5 text-primary shrink-0 mt-0.5" />
                            {r}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-semibold text-sm mb-3 text-foreground">Perks & benefits</h4>
                      <ul className="space-y-2">
                        {role.perks.map(p => (
                          <li key={p} className="text-sm text-muted-foreground flex gap-2">
                            <ArrowRight className="w-3.5 h-3.5 text-primary shrink-0 mt-0.5" />
                            {p}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  <div className="pt-2 border-t flex flex-wrap gap-3">
                    <a
                      href={`https://wa.me/2349040460813?text=Hi%2C%20I'd%20like%20to%20apply%20for%20the%20${encodeURIComponent(role.title)}%20role%20at%20RetailPulse`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Button className="gap-2">
                        <MessageCircle className="w-4 h-4" />
                        Apply via WhatsApp
                      </Button>
                    </a>
                    <p className="text-xs text-muted-foreground self-center">
                      Send a short intro + anything relevant (CV, portfolio, socials) on WhatsApp. No formal cover letter needed.
                    </p>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </section>

      {/* CTA bottom */}
      <section className="bg-primary text-primary-foreground">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-16 text-center">
          <h2 className="text-3xl font-bold mb-4">Don't see your role?</h2>
          <p className="text-primary-foreground/80 mb-8 leading-relaxed max-w-xl mx-auto">
            We're always open to talented people who believe in what we're building.
            Send us a message on WhatsApp and tell us how you'd contribute.
          </p>
          <a
            href="https://wa.me/2349040460813?text=Hi%2C%20I'd%20love%20to%20join%20the%20RetailPulse%20team.%20Here's%20a%20bit%20about%20me..."
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button size="lg" variant="secondary" className="gap-2">
              <MessageCircle className="w-5 h-5" />
              Say hello on WhatsApp
            </Button>
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t py-8 text-center text-sm text-muted-foreground">
        <div className="flex items-center justify-center gap-4 flex-wrap">
          <Link href={`${BASE}/`}><span className="hover:text-foreground cursor-pointer">Home</span></Link>
          <Link href={`${BASE}/changelog`}><span className="hover:text-foreground cursor-pointer">Changelog</span></Link>
          <Link href={`${BASE}/roadmap`}><span className="hover:text-foreground cursor-pointer">Roadmap</span></Link>
          <a href="https://instagram.com/retailpulsepos" target="_blank" rel="noopener noreferrer" className="hover:text-foreground">Instagram</a>
          <a href="https://wa.me/2349040460813" target="_blank" rel="noopener noreferrer" className="hover:text-foreground">WhatsApp</a>
        </div>
        <p className="mt-4">© {new Date().getFullYear()} RetailPulse. All rights reserved.</p>
      </footer>
    </div>
  );
}
